import Typography from "@mui/material/Typography";
import {Divider, FormControlLabel, Grid, Switch} from "@mui/material";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import NotificationItem from "./NotificationItem";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import React, {Dispatch, FC, SetStateAction, useMemo, useState} from "react";
import {markAllNotificationsAsRead} from "../../../store/notification/ActionCreators";
import TranslateSafeLoadingButton from "../../shared/translate-safe-loading-button/TranslateSafeLoadingButton";
import {INotification} from "../../../store/notification/Helper";
import {useTheme} from "@mui/material/styles";
import {StorageConstants} from "../../../util/constants";

interface IProps {
    anchorEl: null | HTMLElement
    setAnchorEl: Dispatch<SetStateAction<null | HTMLElement>>
}

const Notifications: FC<IProps> = (props) => {
    const notifications = useAppSelector((state: RootState) => state.notificationReducer.items)
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const markAllAsReadLoading = useAppSelector((state) => state.notificationReducer.markAllAsReadLoading)
    const [onlyUnread, setOnlyUnread] = useState(sessionStorage.getItem(StorageConstants.NOTIFICATIONS_UNREAD) === "true")
    const theme = useTheme()

    const handleMarkAllAsRead = () => {
        dispatch(markAllNotificationsAsRead())
    }

    const handleOnlyUnreadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        sessionStorage.setItem(StorageConstants.NOTIFICATIONS_UNREAD, String(event.target.checked))
        setOnlyUnread(event.target.checked);
    };

    const displayedNotifications = useMemo(() => {
        if (!notifications)
            return []
        if (onlyUnread)
            return notifications.filter((n: INotification) => !n.read)
        return notifications
    }, [notifications, onlyUnread])

    return (
        <Menu
            id="notifications-menu"
            sx={{
                mt: 1,
                "& .MuiDivider-root": {
                    m: 0
                }
            }}
            anchorEl={props.anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            open={Boolean(props.anchorEl)}
            onClose={() => props.setAnchorEl(null)}>
            <Box sx={{
                maxWidth: 380,
            }}>
                <Typography variant="h6" sx={{color: 'text.secondary'}} fontWeight={600} mx="16px">
                    {t('notifications')}
                </Typography>

                <Grid container justifyContent="space-between" alignItems="center" mb={1} px="16px">
                    <FormControlLabel sx={{
                        pl: '8px',
                        color: theme.palette.secondary.main,
                        "& .MuiTypography-root": {
                            fontSize: '0.8125rem'
                        }
                    }} control={<Switch size="small"
                                        color="secondary"
                                        checked={onlyUnread}
                                        onChange={handleOnlyUnreadChange}
                                        inputProps={{'aria-label': 'Show only unread notifications switch'}}/>}
                                      label={t('only_unread')}/>
                    <TranslateSafeLoadingButton loading={markAllAsReadLoading}
                                   color="secondary"
                                   size="small"
                                   variant="outlined"
                                   onClick={handleMarkAllAsRead}>
                        {t('mark_all_as_read')}
                    </TranslateSafeLoadingButton>
                </Grid>

                <Divider/>
                <Box sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                }}>
                    {displayedNotifications.length ? displayedNotifications.map((n) => (
                        <React.Fragment key={n.id}>
                            <NotificationItem notification={n} setAnchorEl={props.setAnchorEl}/>
                            <Divider/>
                        </React.Fragment>
                    )) : <Typography variant="subtitle2" color="secondary" textAlign="center" px={2} pt="8px">
                        {t('no_notifications')}
                    </Typography>}
                </Box>

            </Box>
        </Menu>
    )
}

export default Notifications